import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { getNftVaultOptions, ouraMutationOptions } from "@/services/queryOptions/ouraQueryOptions";
import { nftMutationSchema } from "@/types/oura";
import { SecurityNote, TextAreaField, TextField } from "../components/FormControls";

type NftValues = z.infer<typeof nftMutationSchema>;

const defaults: NftValues = {
  keypair: "",
  collection: "",
  token_mint: "",
  asset: "",
  recipient: "",
  name: "",
  uri: "",
  confirmed: false,
};

function useNftForm() {
  return useForm<NftValues>({
    resolver: zodResolver(nftMutationSchema),
    defaultValues: defaults,
  });
}

function MintForm({ mode }: { mode: "admin" | "user" | "vip" }) {
  const { toast } = useToast();
  const form = useNftForm();
  const mutation = useMutation(
    ouraMutationOptions<Record<string, unknown>>("POST", `/api/v1/oura/nft/${mode}`),
  );
  const keyName = mode === "admin" ? "admin" : "payer";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{mode.toUpperCase()} Mint</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 lg:grid-cols-2"
          onSubmit={form.handleSubmit((values) => {
            if (!values.name) form.setError("name", { message: "Name is required" });
            if (!values.uri) form.setError("uri", { message: "URI is required" });
            if (!values.name || !values.uri) return;
            mutation.mutate(
              {
                [keyName]: values.keypair,
                collection: values.collection,
                token_mint: values.token_mint,
                name: values.name,
                uri: values.uri,
              },
              {
                onSuccess: (result) => {
                  toast({ title: "NFT mint submitted", description: result.signature });
                  form.reset(defaults);
                },
              },
            );
          })}
        >
          <div className="lg:col-span-2">
            <SecurityNote />
          </div>
          <TextAreaField
            label={`${keyName} secretKey JSON`}
            placeholder="[12,34,56,...]"
            registration={form.register("keypair")}
            error={form.formState.errors.keypair}
          />
          <TextField label="Collection" placeholder="So11111111111111111111111111111111111111112" registration={form.register("collection")} error={form.formState.errors.collection} />
          <TextField label="Token mint" placeholder="So11111111111111111111111111111111111111112" registration={form.register("token_mint")} error={form.formState.errors.token_mint} />
          <TextField label="Name" placeholder="Tweeto NFT #1" registration={form.register("name")} error={form.formState.errors.name} />
          <TextField label="URI" placeholder="https://example.com/nft/1.json" registration={form.register("uri")} error={form.formState.errors.uri} />
          <Button className="lg:col-span-2" disabled={mutation.isPending}>Mint NFT</Button>
        </form>
      </CardContent>
    </Card>
  );
}

function UpdateBurnForceForms() {
  const { toast } = useToast();
  const form = useNftForm();
  const update = useMutation(ouraMutationOptions<Record<string, unknown>>("PUT", "/api/v1/oura/nft"));
  const burn = useMutation(ouraMutationOptions<Record<string, unknown>>("DELETE", "/api/v1/oura/nft"));
  const unlock = useMutation(ouraMutationOptions<Record<string, unknown>>("PATCH", "/api/v1/oura/force-unlock"));

  const submit = (action: "update" | "burn" | "unlock") =>
    form.handleSubmit((values) => {
      if ((action === "update" || action === "burn") && !values.asset) {
        form.setError("asset", { message: "Asset is required" });
        return;
      }
      if (action === "update" && (!values.name || !values.uri)) {
        if (!values.name) form.setError("name", { message: "Name is required" });
        if (!values.uri) form.setError("uri", { message: "URI is required" });
        return;
      }
      if ((action === "burn" || action === "unlock") && !values.confirmed) {
        form.setError("confirmed", { message: "Confirm before submitting" });
        return;
      }

      const body =
        action === "unlock"
          ? { admin: values.keypair, collection: values.collection, token_mint: values.token_mint }
          : {
              payer: values.keypair,
              collection: values.collection,
              asset: values.asset,
              token_mint: values.token_mint,
              name: values.name,
              uri: values.uri,
            };
      const mutation = action === "update" ? update : action === "burn" ? burn : unlock;
      mutation.mutate(body, {
        onSuccess: (result) => {
          toast({ title: "NFT transaction submitted", description: result.signature });
          form.reset(defaults);
        },
      });
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Update, Burn, Force Unlock</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <SecurityNote />
          </div>
          <TextAreaField
            label="Payer/admin secretKey JSON"
            placeholder="[12,34,56,...]"
            registration={form.register("keypair")}
            error={form.formState.errors.keypair}
          />
          <TextField label="Collection" placeholder="So11111111111111111111111111111111111111112" registration={form.register("collection")} error={form.formState.errors.collection} />
          <TextField label="Asset" placeholder="So11111111111111111111111111111111111111112" registration={form.register("asset")} error={form.formState.errors.asset} />
          <TextField label="Token mint" placeholder="So11111111111111111111111111111111111111112" registration={form.register("token_mint")} error={form.formState.errors.token_mint} />
          <TextField label="Name" placeholder="Tweeto NFT #1" registration={form.register("name")} error={form.formState.errors.name} />
          <TextField label="URI" placeholder="https://example.com/nft/1.json" registration={form.register("uri")} error={form.formState.errors.uri} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...form.register("confirmed")} />
            Confirm destructive or vesting action
          </label>
          {form.formState.errors.confirmed && <p className="text-xs text-red-400">{form.formState.errors.confirmed.message}</p>}
          <div className="flex flex-wrap gap-2 lg:col-span-2">
            <Button type="button" onClick={submit("update")} disabled={update.isPending}>Update NFT</Button>
            <Button type="button" variant="destructive" onClick={submit("burn")} disabled={burn.isPending}>Burn NFT</Button>
            <Button type="button" variant="outline" onClick={submit("unlock")} disabled={unlock.isPending}>Force Unlock</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function VaultLookup() {
  const [lookup, setLookup] = useState({ asset: "", collection: "", tokenMint: "" });
  const [active, setActive] = useState(lookup);
  const query = useQuery(getNftVaultOptions(active.asset, active.collection, active.tokenMint));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vault Info Lookup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-3">
          <input className="h-10 rounded border border-input bg-background px-3 text-sm" placeholder="So11111111111111111111111111111111111111112" value={lookup.asset} onChange={(e) => setLookup({ ...lookup, asset: e.target.value })} />
          <input className="h-10 rounded border border-input bg-background px-3 text-sm" placeholder="So11111111111111111111111111111111111111112" value={lookup.collection} onChange={(e) => setLookup({ ...lookup, collection: e.target.value })} />
          <input className="h-10 rounded border border-input bg-background px-3 text-sm" placeholder="So11111111111111111111111111111111111111112" value={lookup.tokenMint} onChange={(e) => setLookup({ ...lookup, tokenMint: e.target.value })} />
        </div>
        <Button type="button" onClick={() => setActive(lookup)}>Lookup</Button>
        {query.data && (
          <pre className="overflow-auto rounded bg-zinc-950 p-4 text-xs">{JSON.stringify(query.data, null, 2)}</pre>
        )}
      </CardContent>
    </Card>
  );
}

export function NftManager() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">NFTs</h2>
        <p className="text-sm text-zinc-400">Mint, update, burn, inspect vault state, and force unlock vesting.</p>
      </div>
      <Tabs defaultValue="admin">
        <TabsList>
          <TabsTrigger value="admin">Admin Mint</TabsTrigger>
          <TabsTrigger value="user">User Mint</TabsTrigger>
          <TabsTrigger value="vip">VIP Mint</TabsTrigger>
          <TabsTrigger value="mutate">Mutations</TabsTrigger>
          <TabsTrigger value="vault">Vault Info</TabsTrigger>
        </TabsList>
        <TabsContent value="admin"><MintForm mode="admin" /></TabsContent>
        <TabsContent value="user"><MintForm mode="user" /></TabsContent>
        <TabsContent value="vip"><MintForm mode="vip" /></TabsContent>
        <TabsContent value="mutate"><UpdateBurnForceForms /></TabsContent>
        <TabsContent value="vault"><VaultLookup /></TabsContent>
      </Tabs>
    </div>
  );
}
